import { lazy, Suspense, type ReactElement } from 'react';
import { Route, Routes } from 'react-router-dom';
import { Spinner } from './components/ui';
import { Layout } from './components/Layout';
import { Home } from './pages/Home';
import { GameHub } from './pages/GameHub';
import { Play } from './pages/Play';
import { Room } from './pages/Room';
import { Matchmaking } from './pages/Matchmaking';
import { Learn, Settings, Profile, History, Replay, Leaderboard, NotFound } from './pages/Misc';
import { About, Privacy, Terms } from './pages/Static';
import { TerritoryHub, TerritoryPlay, TerritoryNewRoom } from './territory/pages';
import { TerritoryRoomPage, TerritoryMatchPage } from './territory/online-pages';
import { TerritoryLearn } from './territory/Learn';
import { SnakeHub, SnakePlay, ArenaNewRoom } from './snake/pages';
import { SnakeRoomPage, SnakeMatchPage } from './snake/online-pages';
import { SnakeLearn } from './snake/Learn';
import { BlocksHub, BlocksPlay } from './blocks/pages';
import { BlocksRoomPage, BlocksMatchPage, BlocksRankedPage } from './blocks/online-pages';
import { BlocksLearn } from './blocks/Learn';

const SudokuPage = lazy(() => import('./sudoku/pages').then((m) => ({ default: m.SudokuPage })));
const SudokuLearn = lazy(() => import('./sudoku/Learn').then((m) => ({ default: m.SudokuLearn })));
const TerritoryFlowPage = lazy(() => import('./territory/flow-page').then((m) => ({ default: m.TerritoryFlowPage })));
const DoodleLearn = lazy(() => import('./doodle/Learn').then((m) => ({ default: m.DoodleLearn })));
const DoodlePage = lazy(() => import('./doodle/pages').then((m) => ({ default: m.DoodlePage })));
const RescuePage = lazy(() => import('./rescue/pages').then((m) => ({ default: m.RescuePage })));
const RescueRoomPage = lazy(() => import('./rescue/pages').then((m) => ({ default: m.RescueRoomPage })));
const TrailPage = lazy(() => import('./trail/pages').then((m) => ({ default: m.TrailPage })));
const RobotPage = lazy(() => import('./robot/pages').then((m) => ({ default: m.RobotPage })));
const RestaurantPage = lazy(() => import('./restaurant/pages').then((m) => ({ default: m.RestaurantPage })));
const TaxiPage = lazy(() => import('./taxi/TaxiPage').then((m) => ({ default: m.TaxiPage })));
const BumperPage = lazy(() => import('./bumper/BumperPage').then((m) => ({ default: m.BumperPage })));
const MathParentPage = lazy(() => import('./math/parent-page').then((m) => ({ default: m.MathParentPage })));
const MathIslandPage = lazy(() => import('./math/island').then((m) => ({ default: m.MathIslandPage })));
const FactoryPage = lazy(() => import('./math/factory-page').then((m) => ({ default: m.FactoryPage })));
const ApplePage = lazy(() => import('./math/apple-page').then((m) => ({ default: m.ApplePage })));
const TrainPage = lazy(() => import('./math/train-page').then((m) => ({ default: m.TrainPage })));
const ShopPage = lazy(() => import('./math/shop-page').then((m) => ({ default: m.ShopPage })));
const BalancePage = lazy(() => import('./math/balance-page').then((m) => ({ default: m.BalancePage })));
const ClockPage = lazy(() => import('./math/clock-page').then((m) => ({ default: m.ClockPage })));
const GardenPage = lazy(() => import('./math/garden-page').then((m) => ({ default: m.GardenPage })));
const FrogPage = lazy(() => import('./math/frog-page').then((m) => ({ default: m.FrogPage })));
const lazyEl = (el: ReactElement) => <Suspense fallback={<Spinner />}>{el}</Suspense>;

function pages(prefix: string) {
  const p = (s: string) => `${prefix}${s}`;
  return [
    <Route key={p('/')} path={p('/') || '/'} element={<Home />} />,
    <Route key={p('/chess')} path={p('/chess')} element={<GameHub game="chess" />} />,
    <Route key={p('/xiangqi')} path={p('/xiangqi')} element={<GameHub game="xiangqi" />} />,
    <Route key={p('/banqi')} path={p('/banqi')} element={<GameHub game="banqi" />} />,
    <Route key={p('/learn/territory')} path={p('/learn/territory')} element={<TerritoryLearn />} />,
    <Route key={p('/learn/snake')} path={p('/learn/snake')} element={<SnakeLearn />} />,
    <Route key={p('/learn/blocks')} path={p('/learn/blocks')} element={<BlocksLearn />} />,
    <Route key={p('/learn/sudoku')} path={p('/learn/sudoku')} element={lazyEl(<SudokuLearn />)} />,
    <Route key={p('/learn')} path={p('/learn/:game')} element={<Learn />} />,
    <Route key={p('/sudoku')} path={p('/sudoku')} element={lazyEl(<SudokuPage />)} />,
    <Route key={p('/learn/doodle')} path={p('/learn/doodle')} element={lazyEl(<DoodleLearn />)} />,
    <Route key={p('/doodle')} path={p('/doodle')} element={lazyEl(<DoodlePage />)} />,
    <Route key={p('/rescue')} path={p('/rescue')} element={lazyEl(<RescuePage />)} />,
    <Route key={p('/rescue/room')} path={p('/rescue/room/:id')} element={lazyEl(<RescueRoomPage />)} />,
    <Route key={p('/trail')} path={p('/trail')} element={lazyEl(<TrailPage />)} />,
    <Route key={p('/robot')} path={p('/robot')} element={lazyEl(<RobotPage />)} />,
    <Route key={p('/restaurant')} path={p('/restaurant')} element={lazyEl(<RestaurantPage />)} />,
    <Route key={p('/taxi')} path={p('/taxi')} element={lazyEl(<TaxiPage />)} />,
    <Route key={p('/bumper')} path={p('/bumper')} element={lazyEl(<BumperPage />)} />,
    <Route key={p('/math')} path={p('/math')} element={lazyEl(<MathIslandPage />)} />,
    <Route key={p('/math/parent')} path={p('/math/parent')} element={lazyEl(<MathParentPage />)} />,
    <Route key={p('/math/g1/apple')} path={p('/math/g1/apple')} element={lazyEl(<ApplePage />)} />,
    <Route key={p('/math/g1/train')} path={p('/math/g1/train')} element={lazyEl(<TrainPage />)} />,
    <Route key={p('/math/g1/shop')} path={p('/math/g1/shop')} element={lazyEl(<ShopPage />)} />,
    <Route key={p('/math/g1/balance')} path={p('/math/g1/balance')} element={lazyEl(<BalancePage />)} />,
    <Route key={p('/math/g1/clock')} path={p('/math/g1/clock')} element={lazyEl(<ClockPage />)} />,
    <Route key={p('/math/g2/garden')} path={p('/math/g2/garden')} element={lazyEl(<GardenPage />)} />,
    <Route key={p('/math/g3/frog')} path={p('/math/g3/frog')} element={lazyEl(<FrogPage />)} />,
    <Route key={p('/math/g3/factory')} path={p('/math/g3/factory')} element={lazyEl(<FactoryPage />)} />,
    <Route key={p('/snake')} path={p('/snake')} element={<SnakeHub />} />,
    <Route key={p('/snake/play')} path={p('/snake/play')} element={<SnakePlay />} />,
    <Route key={p('/snake/new-room')} path={p('/snake/new-room')} element={<ArenaNewRoom game="snake" />} />,
    <Route key={p('/snake/room')} path={p('/snake/room/:id')} element={<SnakeRoomPage />} />,
    <Route key={p('/snake/match')} path={p('/snake/match')} element={<SnakeMatchPage />} />,
    <Route key={p('/blocks')} path={p('/blocks')} element={<BlocksHub />} />,
    <Route key={p('/blocks/play')} path={p('/blocks/play')} element={<BlocksPlay />} />,
    <Route key={p('/blocks/new-room')} path={p('/blocks/new-room')} element={<ArenaNewRoom game="blocks" />} />,
    <Route key={p('/blocks/room')} path={p('/blocks/room/:id')} element={<BlocksRoomPage />} />,
    <Route key={p('/blocks/match')} path={p('/blocks/match')} element={<BlocksMatchPage />} />,
    <Route key={p('/blocks/ranked')} path={p('/blocks/ranked')} element={<BlocksRankedPage />} />,
    <Route key={p('/territory')} path={p('/territory')} element={<TerritoryHub />} />,
    <Route key={p('/territory/play')} path={p('/territory/play')} element={<TerritoryPlay />} />,
    <Route key={p('/territory/flow')} path={p('/territory/flow')} element={lazyEl(<TerritoryFlowPage />)} />,
    <Route key={p('/territory/new-room')} path={p('/territory/new-room')} element={<TerritoryNewRoom />} />,
    <Route key={p('/territory/room')} path={p('/territory/room/:id')} element={<TerritoryRoomPage />} />,
    <Route key={p('/territory/match')} path={p('/territory/match')} element={<TerritoryMatchPage />} />,
    <Route key={p('/play')} path={p('/play')} element={<Play />} />,
    <Route key={p('/room')} path={p('/room/:id')} element={<Room />} />,
    <Route key={p('/matchmaking')} path={p('/matchmaking')} element={<Matchmaking />} />,
    <Route key={p('/profile')} path={p('/profile')} element={<Profile />} />,
    <Route key={p('/history')} path={p('/history')} element={<History />} />,
    <Route key={p('/history/id')} path={p('/history/:id')} element={<Replay />} />,
    <Route key={p('/leaderboard')} path={p('/leaderboard')} element={<Leaderboard />} />,
    <Route key={p('/settings')} path={p('/settings')} element={<Settings />} />,
    <Route key={p('/about')} path={p('/about')} element={<About />} />,
    <Route key={p('/privacy')} path={p('/privacy')} element={<Privacy />} />,
    <Route key={p('/terms')} path={p('/terms')} element={<Terms />} />,
  ];
}

export function App() {
  return (
    <Layout>
      <Routes>
        {pages('')}
        {pages('/en')}
        <Route path="/en" element={<Home />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Layout>
  );
}
